



































































































































































































































































import { AddressService } from '@/services/address/addressService';
import { PlantBuilderFormStepContent } from '@/api/interfaces/content/form/steps/plantBuilderFormStepContent';
import { getFormStepMixin } from '@/mixins/formStepMixin';
import FormSelect from '@/components/molecules/formSelect/FormSelect.vue';
import FormField from '@/components/molecules/formField/FormField.vue';
import FormRadioGroup from '@/components/molecules/formRadioGroup/FormRadioGroup.vue';
import FormInputText from '@/components/atoms/formInputText/FormInputText.vue';
import FormBooleanCheckbox from '@/components/molecules/formBooleanCheckbox/FormBooleanCheckbox.vue';
import DatePicker from '@/components/molecules/datePicker/DatePicker.vue';
import FormInputTextWithSuggestions from '@/components/atoms/formInputTextWithSuggestions/FormInputTextWithSuggestions.vue';
import ButtonSpacer from '@/components/molecules/buttonSpacer/ButtonSpacer.vue';
import { PlantBuilderFormStep } from '@/types/forms/formSteps/plantBuilderFormStep';
import { FormStepConfiguration } from '@/api/interfaces/configuration/form/base/formStepConfiguration';
import { OptionsContent } from '@/api/interfaces/content/form/base/optionsContent';
import { LabelContent } from '@/api/interfaces/content/form/base/labelContent';
import { InfoIconContent } from '@/api/interfaces/content/form/base/infoIconContent';
import { contentServices } from '@/services/contentService';
import { phoneNumberToDigits } from '@/utilities/stringParsingUtils';
import { authService } from '@/services/authService';
import {
    buildInstallerOptionKey,
    parseInstallerOptionKey,
} from '@/services/form/config/application/plantBuilder';
import { appState } from '@/services/form/appState';

export default getFormStepMixin<
    PlantBuilderFormStep,
    PlantBuilderFormStepContent,
    FormStepConfiguration
>().extend({
    name: 'PlantBuilderForm',
    components: {
        FormField,
        FormRadioGroup,
        FormInputText,
        FormSelect,
        FormBooleanCheckbox,
        FormInputTextWithSuggestions,
        DatePicker,
        ButtonSpacer,
    },
    computed: {
        selectedInstallerSummary(): string {
            const selectedKey = this.model.installer.value;

            if (!selectedKey) {
                return '';
            }

            const label = this.installersContent.options?.[selectedKey]?.label;

            return label ?? selectedKey;
        },
        installersContent(): OptionsContent {
            const options: Record<string, LabelContent & InfoIconContent> = {};

            if (authService.deaAdditionalUserData?.isInstaller) {
                const entries = authService.deaAdditionalUserData.installers.flatMap(
                    (installer) =>
                        installer.companies.map((company) => ({
                            installer,
                            company,
                        })),
                );

                entries.sort(
                    (a, b) =>
                        a.company.companyName.localeCompare(
                            b.company.companyName,
                        ) || a.company.city.localeCompare(b.company.city),
                );

                entries.forEach(({ installer, company }) => {
                    options[
                        buildInstallerOptionKey(installer.id, company.id)
                    ] = {
                        label: `${company.companyName}, ${company.city}`,
                    };
                });

                return {
                    ...this.content.installer,
                    options,
                };
            }

            const companies =
                contentServices.form.content?.serviceDrivenContent
                    .installersContent;

            if (!companies) {
                return { ...this.content.installer, options: {} };
            }

            const sorted = [...companies].sort(
                (a, b) =>
                    a.companyName.localeCompare(b.companyName) ||
                    a.city.localeCompare(b.city),
            );

            sorted.forEach((company) => {
                options[company.id] = {
                    label: `${company.companyName}, ${company.city}`,
                };
            });

            return {
                ...this.content.installer,
                options,
            };
        },
    },
    data() {
        return {
            addressService: new AddressService(),
            clearableSuggestionFields: {
                zip: {},
                city: {},
                street: {},
            },
        };
    },
    created(): void {
        // When editing/re-running the form the installer select (excludeFromDto)
        // is not restored. Rebuild its value from the persisted companyId
        // (and installerId for installers) so the correct company is preselected.
        const companyId = this.model.companyId.value;

        if (this.model.installer.value || !companyId) {
            return;
        }

        const installerId = this.model.installerId.value ?? '';

        // If the previously selected installer/company no longer exists in the
        // list, keep the restored field values, but mark "not in list" and clear
        // the ids so no stale reference is submitted.
        if (!this.installerExists(installerId, companyId)) {
            this.model.installerNotListed.value = true;
            this.model.companyId.value = '';
            this.model.installerId.value = '';
            return;
        }

        this.model.installer.value = authService.deaAdditionalUserData
            ?.isInstaller
            ? buildInstallerOptionKey(installerId, companyId)
            : companyId;
    },
    watch: {
        'model.installer.value': {
            handler(): void {
                this.updateInstallerFieldValues();
            },
            immediate: false,
        },
        'model.installerNotListed.value': {
            async handler(installerNotListed: boolean): Promise<void> {
                if (appState.debugFillInProgress) {
                    return;
                }

                if (installerNotListed) {
                    this.resetInstallerFieldValues();
                    return;
                }

                this.updateInstallerFieldValues();
            },
            immediate: false,
        },
    },
    methods: {
        installerExists(installerId: string, companyId: string): boolean {
            if (authService.deaAdditionalUserData?.isInstaller) {
                return authService.deaAdditionalUserData.installers.some(
                    (installer) =>
                        installer.id === installerId &&
                        installer.companies.some((c) => c.id === companyId),
                );
            }

            return !!contentServices.form.content?.serviceDrivenContent.installersContent?.some(
                (c) => c.id === companyId,
            );
        },
        resetInstallerFieldValues(): void {
            this.model.installer.value = '';
            this.model.companyId.value = '';
            this.model.installerId.value = '';
            this.model.companyName.value = '';
            this.model.builderName.firstName.value = '';
            this.model.builderName.lastName.value = '';
            this.model.zipAndCity.zip.value = '';
            this.model.zipAndCity.city.value = '';
            this.model.streetAndHousenumber.street.value = '';
            this.model.streetAndHousenumber.housenumber.value = '';
            this.model.email.value = '';
            this.model.phoneNumber1.value = '';
            this.model.phoneNumber2.value = '';
        },
        updateInstallerFieldValues(): void {
            const selectedKey = this.model.installer.value;

            if (!selectedKey) {
                return;
            }

            const { installerId, companyId } = parseInstallerOptionKey(
                selectedKey,
            );

            // Resolve the company: for installers from their own data (matched by
            // installerId and companyId), for end-users from content.
            const matchedInstaller = authService.deaAdditionalUserData
                ?.isInstaller
                ? authService.deaAdditionalUserData.installers.find(
                      (i) => i.id === installerId,
                  )
                : undefined;

            const matchedCompany = authService.deaAdditionalUserData
                ?.isInstaller
                ? matchedInstaller?.companies.find((c) => c.id === companyId)
                : contentServices.form.content?.serviceDrivenContent.installersContent?.find(
                      (c) => c.id === companyId,
                  );

            this.model.companyId.value = matchedCompany?.id ?? '';
            this.model.companyName.value = matchedCompany?.companyName ?? '';
            this.model.zipAndCity.zip.value = matchedCompany?.zipCode ?? '';
            this.model.zipAndCity.city.value = matchedCompany?.city ?? '';
            this.model.streetAndHousenumber.street.value =
                matchedCompany?.street ?? '';
            this.model.streetAndHousenumber.housenumber.value =
                matchedCompany?.houseNumber ?? '';
            this.model.phoneNumber1.value = phoneNumberToDigits(
                matchedCompany?.phoneNumbers?.[0] ?? '',
            );
            this.model.phoneNumber2.value = phoneNumberToDigits(
                matchedCompany?.phoneNumbers?.[1] ?? '',
            );

            if (authService.deaAdditionalUserData?.isInstaller) {
                this.model.installerId.value = matchedInstaller?.id ?? '';
                this.model.builderName.firstName.value =
                    matchedInstaller?.firstName ?? '';
                this.model.builderName.lastName.value =
                    matchedInstaller?.lastName ?? '';
                this.model.email.value =
                    matchedInstaller?.email || matchedCompany?.email || '';
            } else {
                this.model.installerId.value = '';
                this.model.email.value = matchedCompany?.email ?? '';
            }
        },
        async getZipSuggestions(query: string): Promise<string[]> {
            return this.model.country.value === 'deu'
                ? this.addressService.getZipSuggestions(
                      query,
                      this.model.zipAndCity,
                      this.model.streetAndHousenumber,
                  )
                : [];
        },
        async getCitySuggestions(query: string): Promise<string[]> {
            return this.model.country.value === 'deu'
                ? this.addressService.getCitySuggestions(
                      query,
                      this.model.zipAndCity,
                      this.model.streetAndHousenumber,
                  )
                : [];
        },
        async getStreetSuggestions(query: string): Promise<string[]> {
            return this.model.country.value === 'deu'
                ? this.addressService.getStreetSuggestions(
                      query,
                      this.model.zipAndCity,
                  )
                : [];
        },
        handleZipBlur() {
            this.resetSuggestions();

            this.getZipSuggestions(this.model.zipAndCity.zip.value ?? '').then(
                () => {
                    this.model.zipAndCity.city.value = this.addressService.setCity();
                },
            );
        },
        resetSuggestions() {
            this.addressService.clearSuggestionFields(
                this.clearableSuggestionFields,
            );
        },
    },
});
